import { cn } from '$components'
import { ElementType, Fragment, ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Text } from '$components'
import { ChevronDownIcon } from '$components'
import { useStoredState } from '$components'
import { StoredState } from 'src/enums/stored-state'

export type ExpandableNavigationLinkProps = {
  to: string,
  children?: ReactNode,
  icon: ElementType,
  onClick?: () => void,
  subLinks?: Array<{
    to: string,
    children: ReactNode,
  }>,
}

export function ExpandableNavigationLink({
  to,
  children,
  icon: Icon,
  onClick,
  subLinks,
}: ExpandableNavigationLinkProps) {
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useStoredState(StoredState.MobileNavSettingsExpanded, true)

  // Check if any sub-link is active to keep the main link expanded
  const isAnySubLinkActive = subLinks?.some(subLink =>
    location.pathname.startsWith(subLink.to),
  ) || false

  // Auto-expand if any sub-link is active
  const shouldBeExpanded = isExpanded || isAnySubLinkActive

  const handleMainLinkClick = () => {
    if (subLinks && subLinks.length > 0) {
      setIsExpanded(!isExpanded)
    } else {
      onClick?.()
    }
  }

  const handleSubLinkClick = () => {
    onClick?.()
  }

  return (
    <div>
      <NavLink
        to={subLinks && subLinks.length > 0 ? '#' : to}
        onClick={handleMainLinkClick}
        className={({ isActive }) => cn(
          'flex gap-stack-tight p-stack items-center rounded-card-sm',
          {
            'bg-fg text-fg-inverse': isActive && (!subLinks || subLinks.length === 0),
            'hover:bg-bg-subtle text-fg': !isActive || (subLinks && subLinks.length > 0),
          },
        )}
      >
        {({ isActive }) => (
          <Fragment>
            <Icon className={cn(
              'size-[20px] min-w-[20px] min-h-[20px]',
              {
                'text-fg-inverse': isActive && (!subLinks || subLinks.length === 0),
              },
            )}
            />

          <Text
            type="s"
            className={cn(
              'truncate flex-1',
              {
                'text-fg-inverse': isActive && (!subLinks || subLinks.length === 0),
              },
            )}
          >
            {children}
          </Text>

            {subLinks && subLinks.length > 0 && (
              <ChevronDownIcon
                className={cn(
                  'size-4 transition-transform duration-control',
                  {
                    'rotate-180': shouldBeExpanded,
                  },
                )}
              />
            )}
          </Fragment>
        )}
      </NavLink>

      {/* Sub-links */}
      {subLinks && subLinks.length > 0 && shouldBeExpanded && (
        <div className="ml-stack-grid mt-stack-dense space-y-stack-dense">
          {subLinks.map((subLink, index) => (
            <NavLink
              key={index}
              to={subLink.to}
              onClick={handleSubLinkClick}
              className={({ isActive }) => cn(
                'flex p-stack-tight items-center rounded-surface text-body-sm',
                {
                  'bg-fg text-fg-inverse': isActive,
                  'hover:bg-bg-subtle text-fg-muted': !isActive,
                },
              )}
            >
              <Text
                type="s"
                className={cn(
                  'truncate',
                  {
                    'text-fg-inverse': location.pathname.startsWith(subLink.to),
                  },
                )}
              >
                {subLink.children}
              </Text>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}
